import { isNumber } from 'lodash';
import { createSelector } from 'reselect';

import { AccountProfile } from '../models';
import { selectStore } from './store';
import { selectFeatureFlags } from '@common/user-permissions';

export const selectOrganization = createSelector(
  selectStore,
  (s) => s.organization
);
export const selectOrganizationName = createSelector(
  selectStore,
  (s) => s.organization?.name
);

export const selectOrganizationId = createSelector(
  selectStore,
  (s) => s.organization?.id
);

export const selectMediaUsage = createSelector(
  selectOrganization,
  (o) => o.mediaUsageMs || 0
);

export const selectJsonData = createSelector(
  selectOrganization,
  (o) => o.jsondata
);

export const selectHasFreeTrial = createSelector(
  selectJsonData,
  (j) => j.accountProfile === AccountProfile.TRIAL
);

export const selectHasBusinessSubscription = createSelector(
  selectJsonData,
  (j) => j.accountProfile === AccountProfile.BUSINESS
);

export const selectHasEnterprise = createSelector(
  selectHasFreeTrial,
  selectHasBusinessSubscription,
  (f, b) => !f && !b
);

export const selectMediaProcessingLimitMs = createSelector(
  selectJsonData,
  selectHasFreeTrial,
  (f, isTrial) =>
    isTrial ? f.billing?.mediaProcessingLimitMs || 2 * 60 * 60_000 : Infinity
);

export const selectMediaProcessedMs = createSelector(
  selectJsonData,
  (f) => f.billing?.mediaProcessedMs || 0
);

export const selectMediaProcessedLastUpdate = createSelector(
  selectJsonData,
  (f) => f.billing?.mediaProcessedMs_lastUpdated || new Date().toISOString()
);

export const selectMediaProcessedRemainingMs = createSelector(
  selectMediaProcessingLimitMs,
  selectMediaProcessedMs,
  selectHasFreeTrial,
  (mediaProcessingLimitMs, mediaProcessedMs, isFreeTrial) =>
    isFreeTrial
      ? Math.max(0, mediaProcessingLimitMs - mediaProcessedMs)
      : Infinity
);

export const selectMediaHoursUsed = createSelector(
  selectMediaUsage,
  selectHasEnterprise,
  (mediaUsage, isEnterprise) => {
    if (!isEnterprise && isNumber(mediaUsage)) {
      return Number((mediaUsage / (1000 * 60 * 60)).toFixed(1));
    }
    return 0;
  }
);

export const selectIsUsageLimitExceeded = createSelector(
  selectMediaProcessedRemainingMs,
  (remainingMs) => remainingMs <= 0
);

export const selectEnableFolderView = createSelector(
  selectStore,
  selectFeatureFlags,
  (s, featureFlags) =>
    s?.organization?.jsondata?.features?.redact?.enableFolderView || // legacy location
    featureFlags.folderView || // app config - entire org
    featureFlags.folderViewUser || // app config - individual user
    false
);

export const selectEnableAutoColumn = createSelector(
  selectStore,
  (s) => s?.organization?.jsondata?.features?.redact?.enableAutoColumn || false
);

// Thumbnail-tile view mode for a request's files in the folder view (the
// shared MediaTile grid). Only meaningful when the folder view itself is on.
export const selectEnableFolderViewTiles = createSelector(
  selectFeatureFlags,
  (featureFlags) =>
    featureFlags.folderViewTiles || // app config - entire org
    featureFlags.folderViewTilesUser || // app config - individual user
    false
);

// VE-21114: whether the org is on Folders v2. Gates all v2 folderPath/delete
// compatibility handling so v1 orgs are unaffected during the gradual rollout.
//
// The org is enabled at the platform level via
// `updateOrganization(metadata:{features:{v2FoldersEnabled:"enabled"}})`, which
// surfaces on the org's `jsondata.features` (the same shape
// selectEnableFolderView reads) as the string "enabled". That org flag does NOT
// flow into applicationConfig/selectFeatureFlags, so we must read it directly —
// otherwise every gated fix stays dormant on a real v2 org. The app-config /
// static featureFlag is the fallback (and how local dev forces it on).
export const selectV2FoldersEnabled = createSelector(
  selectStore,
  selectFeatureFlags,
  (s, featureFlags) => {
    const orgFlag = s?.organization?.jsondata?.features?.v2FoldersEnabled;
    return (
      orgFlag === true ||
      orgFlag === 'enabled' ||
      orgFlag === 'true' ||
      !!featureFlags.v2FoldersEnabled
    );
  }
);
